import { Input, useDebounceCallback } from "@devhop/ui";
import { SearchIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useEffect, useState } from "react";
import type { ListOTRulesQuery } from "@/modules/hr/overtime/domain/types";

export type OTRulesSearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  dayType?: "weekday" | "holiday";
  isActive?: boolean;
  search?: string;
};

const VALUE_ALL = "__all__";
const DAY_TYPE_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "weekday", label: "ວັນທຳມະດາ" },
  { value: "holiday", label: "ວັນພັກ" },
];
const STATUS_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "true", label: "ACTIVE" },
  { value: "false", label: "INACTIVE" },
];

type OTRulesFilterProps = {
  searchParams: OTRulesSearchParams;
  fromRoute: "/app/hr/overtime/rules";
};

export function OTRulesFilter({ searchParams, fromRoute }: OTRulesFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [dayTypeInput, setDayTypeInput] = useState(
    search.dayType ?? VALUE_ALL,
  );
  const [statusInput, setStatusInput] = useState(
    search.isActive === true ? "true" : search.isActive === false ? "false" : VALUE_ALL,
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setDayTypeInput(search.dayType ?? VALUE_ALL);
    setStatusInput(
      search.isActive === true ? "true" : search.isActive === false ? "false" : VALUE_ALL,
    );
  }, [search.search, search.dayType, search.isActive]);

  const doNav = (opts: { dayType?: string; status?: string; searchVal?: string } = {}) => {
    const dt = opts.dayType ?? dayTypeInput;
    const st = opts.status ?? statusInput;
    const sv = opts.searchVal ?? searchInput;
    nav({
      search: {
        offset: 0,
        limit: search.limit ?? 20,
        sort: search.sort,
        dayType: dt === VALUE_ALL ? undefined : (dt as "weekday" | "holiday"),
        isActive: st === VALUE_ALL ? undefined : st === "true",
        search: sv.trim() || undefined,
      },
    });
  };

  const debouncedSearch = useDebounceCallback((val: string) => doNav({ searchVal: val }), 400);

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="ot-rules-search"
            aria-label="ຄົ້ນຫາຕາມປະເພດວັນ ຫຼື ສະຖານະ"
            placeholder="ຄົ້ນຫາຂໍ້ກຳນົດ OT..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <SimpleSelect
          options={DAY_TYPE_OPTIONS}
          value={dayTypeInput}
          onValueChange={(v) => {
            setDayTypeInput(v);
            doNav({ dayType: v });
          }}
          placeholder="ປະເພດວັນ"
          className="h-8 w-full sm:w-32"
          aria-label="ກັ່ນຕອງຕາມປະເພດວັນ"
        />
        <SimpleSelect
          options={STATUS_OPTIONS}
          value={statusInput}
          onValueChange={(v) => {
            setStatusInput(v);
            doNav({ status: v });
          }}
          placeholder="ສະຖານະ"
          className="h-8 w-full sm:w-32"
          aria-label="ກັ່ນຕອງຕາມສະຖານະ"
        />
      </div>
    </div>
  );
}
